import {
    Box,
    Button,
    Flex,
    Heading,
    HStack,
    Image,
    SimpleGrid,
    Skeleton,
    SkeletonText,
    SkeletonCircle,
    Text,
    VStack,
    Badge,
    Tooltip,
    // Divider,
} from '@chakra-ui/react';
import { useMemo, memo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PreOffersContainer } from 'components/LandingPageComponents/styles/SplashStyles';
import placeholder from '../../assets/videoPlaceholder.png';
import ProductItemNew from 'components/Dashboard/ProductItemNew';
import Brands from 'components/Dashboard/Brands';
import { useVendors, normalizeVendor, buildVendorUrl, isDisplayableVendor } from 'hooks/useVendors';
import useGetUserDetails from 'hooks/useGetUserDetail';
import { useGetProducts } from 'hooks/useProducts';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';

const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';
const SHADOW_MD = '0 4px 16px rgba(27,59,95,0.08)';

const FONT = "'Bricolage Grotesque', sans-serif";

/* ─────────────────────────────────────────────────────────────
   SUPPORT
   ───────────────────────────────────────────────────────────── */
const SUPPORT_WHATSAPP_NUMBER =
    (typeof process !== 'undefined' && process.env?.REACT_APP_SUPPORT_WHATSAPP) ||
    '2348000000000';

const SUPPORT_DEFAULT_MESSAGE = 'Hi Obana team, I need help with my dashboard.';

const openWhatsAppSupport = (message = SUPPORT_DEFAULT_MESSAGE) => {
    const url = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
};

/* ─────────────────────────────────────────────────────────────
   VENDOR NAVIGATION — single source of truth
   ───────────────────────────────────────────────────────────── */
const useGoToVendor = () => {
    const navigate = useNavigate();
    return useCallback(
        (vendor) => {
            if (!vendor) return;
            navigate(buildVendorUrl(vendor.raw ?? vendor));
        },
        [navigate]
    );
};

/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const SparkleIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M12 2L13.5 9H20L14.5 13.5L16.5 21L12 17L7.5 21L9.5 13.5L4 9H10.5L12 2Z" />
    </svg>
);
const TagIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M21.41 11.58L12.41 2.58C12.04 2.21 11.53 2 11 2H4C2.9 2 2 2.9 2 4V11C2 11.53 2.21 12.04 2.59 12.42L11.59 21.42C11.95 21.79 12.44 22 13 22C13.56 22 14.05 21.79 14.41 21.41L21.41 14.41C21.79 14.05 22 13.56 22 13C22 12.44 21.78 11.95 21.41 11.58ZM6.5 7.5C5.67 7.5 5 6.83 5 6C5 5.17 5.67 4.5 6.5 4.5C7.33 4.5 8 5.17 8 6C8 6.83 7.33 7.5 6.5 7.5Z" />
    </svg>
);
const TrophyIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M19 5H17V3H7V5H5C3.9 5 3 5.9 3 7V8C3 10.55 4.92 12.63 7.39 12.94C8.02 14.44 9.37 15.57 11 15.9V18H8V20H16V18H13V15.9C14.63 15.57 15.98 14.44 16.61 12.94C19.08 12.63 21 10.55 21 8V7C21 5.9 20.1 5 19 5Z" />
    </svg>
);
const ClockIcon = (props) => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" {...props}>
        <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM12.5 7H11V13L16.25 16.15L17 14.92L12.5 12.25V7Z" />
    </svg>
);
const ChevronRightIcon = (props) => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const StoreIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M3 9l1.5-5h15L21 9 M3 9v11h18V9 M3 9h18 M8 14h8" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
);
const PanelCloseIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const PanelOpenIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   SECTION CONFIG
   ───────────────────────────────────────────────────────────── */
const SECTIONS = [
    {
        key: 'new_arrivals',
        label: 'New Arrivals',
        subtitle: 'Fresh stock, just landed',
        icon: SparkleIcon,
        accent: NAVY,
        lightBg: CYAN,
        badgeText: 'NEW',
        query: { filter: 'new_arrivals' },
        seeAllUrl: '/dashboard/products?filter=new_arrivals',
    },
    {
        key: 'best_sellers',
        label: 'Best Sellers',
        subtitle: 'Top picks from other agents',
        icon: TrophyIcon,
        accent: '#C07B2A',
        lightBg: '#FDF5E9',
        badgeText: 'HOT',
        query: { filter: 'best_sellers' },
        seeAllUrl: '/dashboard/products?filter=best_sellers',
    },
    {
        key: 'preorder',
        label: 'Preorder Offers',
        subtitle: 'Reserve before stock runs out',
        icon: ClockIcon,
        accent: '#5B4FCF',
        lightBg: '#F0EEFF',
        badgeText: 'PRE-ORDER',
        query: { filter: 'preorder' },
        seeAllUrl: '/dashboard/products?filter=preorder',
    },
    {
        key: 'clearance',
        label: 'Clearance Sales',
        subtitle: 'Deep discounts, while stocks last',
        icon: TagIcon,
        accent: '#C53030',
        lightBg: '#FFF0F0',
        badgeText: 'SALE',
        query: { filter: 'clearance' },
        seeAllUrl: '/dashboard/products?filter=clearance',
    },
];

const ITEMS_PER_SECTION = 10;
const SKELETON_KEYS = Array.from({ length: ITEMS_PER_SECTION }, (_, i) => i);

const NEW_ARRIVALS_CONFIG = SECTIONS[0];
const TRAILING_SECTIONS   = SECTIONS.slice(1);

const SHOP_BY_VENDOR_COUNT  = 8;
const TOP_VENDORS_COUNT     = 8;

/* ─────────────────────────────────────────────────────────────
   SHARED PRIMITIVES
   ───────────────────────────────────────────────────────────── */
const Card = memo(function Card({ children, p, ...rest }) {
    return (
        <Box
            bg={SURFACE}
            borderRadius="14px"
            p={p ?? { base: '14px', md: '16px' }}
            border="1px solid"
            borderColor={BORDER}
            boxShadow={SHADOW_SM}
            transition="box-shadow 0.2s ease"
            {...rest}
        >
            {children}
        </Box>
    );
});

const SectionTitle = memo(function SectionTitle({
    title, subtitle, icon: Icon, accent = NAVY, lightBg, badge, onSeeAll,
}) {
    return (
        <Flex align="center" justify="space-between" mb="10px" gap="10px">
            <HStack spacing="8px" minW="0" flex="1">
                {Icon && (
                    <Flex
                        align="center"
                        justify="center"
                        bg={accent}
                        color="white"
                        borderRadius="7px"
                        w="24px"
                        h="24px"
                        flexShrink={0}
                    >
                        <Icon />
                    </Flex>
                )}
                <Box minW="0" flex="1">
                    <Flex align="center" gap="6px" flexWrap="wrap">
                        <Heading
                            fontSize="14px"
                            fontWeight="800"
                            color={INK}
                            lineHeight="1.2"
                            fontFamily={FONT}
                            letterSpacing="-0.01em"
                        >
                            {title}
                        </Heading>
                        {badge && (
                            <Badge
                                bg={`${accent}15`}
                                color={accent}
                                fontSize="8px"
                                fontWeight="800"
                                px="5px"
                                py="1.5px"
                                borderRadius="4px"
                                letterSpacing="0.06em"
                                border={`1px solid ${accent}30`}
                            >
                                {badge}
                            </Badge>
                        )}
                    </Flex>
                    {subtitle && (
                        <Text fontSize="10.5px" color={FAINT} mt="1px" fontWeight="500">
                            {subtitle}
                        </Text>
                    )}
                </Box>
            </HStack>
            {onSeeAll && (
                <Button
                    variant="ghost"
                    size="xs"
                    color={accent}
                    fontWeight="600"
                    fontSize="10.5px"
                    px="6px"
                    h="24px"
                    rightIcon={<ChevronRightIcon />}
                    _hover={{ bg: lightBg ?? SURFACE_ALT }}
                    borderRadius="6px"
                    onClick={onSeeAll}
                    flexShrink={0}
                >
                    See all
                </Button>
            )}
        </Flex>
    );
});

const ProductSkeleton = memo(function ProductSkeleton() {
    return (
        <Box borderRadius="8px" overflow="hidden" bg={SURFACE} border="1px solid" borderColor={BORDER}>
            <Skeleton h="120px" w="full" startColor={SURFACE_ALT} endColor={BORDER} />
            <Box p="8px">
                <SkeletonText noOfLines={2} spacing="1.5" skeletonHeight="1.5" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton mt="1.5" h="3.5" w="50%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Box>
        </Box>
    );
});

const EmptySection = memo(function EmptySection({ label, lightBg, icon: Icon, onBrowse }) {
    return (
        <Flex
            direction="column"
            align="center"
            justify="center"
            bg={lightBg}
            borderRadius="10px"
            p="22px 18px"
            textAlign="center"
            gridColumn="1 / -1"
            border="1px dashed"
            borderColor={BORDER}
        >
            <Flex
                w="32px"
                h="32px"
                borderRadius="8px"
                bg={SURFACE}
                align="center"
                justify="center"
                mb="8px"
                color={MUTED}
                boxShadow={SHADOW_SM}
            >
                {Icon && <Icon />}
            </Flex>
            <Text fontSize="12px" fontWeight="600" color={INK} mb="1px">
                No {label.toLowerCase()} yet
            </Text>
            <Text fontSize="10.5px" color={FAINT} mb="8px">
                Check back soon — fresh stock arrives daily.
            </Text>
            {onBrowse && (
                <Button size="xs" variant="outline" borderColor={BORDER} color={NAVY} onClick={onBrowse} fontSize="10.5px" h="26px">
                    Browse all products
                </Button>
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   GREETING BANNER
   ───────────────────────────────────────────────────────────── */
const GREETING_WORDS = {
    morning:   { word: 'Good morning',   emoji: '☀️' },
    afternoon: { word: 'Good afternoon', emoji: '🌤️' },
    evening:   { word: 'Good evening',   emoji: '🌙' },
};

const getTimeOfDay = () => {
    const h = new Date().getHours();
    if (h < 12) return 'morning';
    if (h < 17) return 'afternoon';
    return 'evening';
};

const GreetingBanner = memo(function GreetingBanner({ name, navigate }) {
    const tod = useMemo(getTimeOfDay, []);
    const { word, emoji } = GREETING_WORDS[tod];
    return (
        <Flex align="center" justify="space-between" mb="14px" gap="14px" flexWrap="wrap">
            <Box minW="0">
                <Flex align="center" gap="6px" mb="1px">
                    <Box fontSize="16px" lineHeight="1">{emoji}</Box>
                    <Text
                        fontSize="10.5px"
                        color={FAINT}
                        fontWeight="600"
                        letterSpacing="0.06em"
                        textTransform="uppercase"
                    >
                        {word}
                    </Text>
                </Flex>
                <Heading
                    fontSize={{ base: '18px', md: '20px' }}
                    color={INK}
                    fontWeight="800"
                    fontFamily={FONT}
                    letterSpacing="-0.02em"
                    lineHeight="1.2"
                    textTransform="capitalize"
                >
                    Welcome back, {name}
                </Heading>
            </Box>
            <Button
                onClick={() => navigate('/dashboard/products')}
                bg={NAVY}
                color="white"
                _hover={{ bg: NAVY_DEEP }}
                rightIcon={<ChevronRightIcon />}
                borderRadius="8px"
                h="34px"
                px="14px"
                fontSize="11.5px"
                fontWeight="700"
                fontFamily={FONT}
                flexShrink={0}
            >
                Browse products
            </Button>
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   VENDOR AVATAR
   ───────────────────────────────────────────────────────────── */
const VendorAvatar = memo(function VendorAvatar({ vendor, size = '48px', fontSize = '18px' }) {
    const initial = (vendor.name?.[0] ?? '?').toUpperCase();
    return (
        <Flex
            w={size}
            h={size}
            borderRadius="full"
            bg={vendor.image ? SURFACE : CYAN}
            color={NAVY}
            align="center"
            justify="center"
            fontWeight="800"
            fontSize={fontSize}
            fontFamily={FONT}
            overflow="hidden"
            border="1px solid"
            borderColor={BORDER}
            flexShrink={0}
        >
            {vendor.image ? (
                <Image
                    src={vendor.image}
                    alt={vendor.name}
                    w="full"
                    h="full"
                    objectFit="cover"
                    fallback={
                        <Flex w="full" h="full" align="center" justify="center" bg={CYAN}>
                            {initial}
                        </Flex>
                    }
                />
            ) : (
                initial
            )}
        </Flex>
    );
});

/* ─────────────────────────────────────────────────────────────
   SHOP BY VENDOR (horizontal strip)
   ───────────────────────────────────────────────────────────── */
const ShopByVendorSkeleton = memo(function ShopByVendorSkeleton() {
    return (
        <Flex direction="column" align="center" gap="6px" flexShrink={0} minW="80px" p="6px">
            <SkeletonCircle size="52px" startColor={SURFACE_ALT} endColor={BORDER} />
            <Skeleton h="2.5" w="60%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
        </Flex>
    );
});

const ShopByVendor = memo(function ShopByVendor({ navigate }) {
    const { vendors: rawVendors, isLoading } = useVendors({ per_page: 30 });
    const vendors = useMemo(
        () => rawVendors.filter(isDisplayableVendor).map(normalizeVendor),
        [rawVendors]
    );
    const goToVendor = useGoToVendor();
    const displayVendors = vendors.slice(0, SHOP_BY_VENDOR_COUNT);

    return (
        <Card mb="14px">
            <SectionTitle
                title="Shop by Vendor"
                subtitle="Browse products from verified sellers"
                icon={StoreIcon}
                accent={NAVY}
                lightBg={CYAN}
                onSeeAll={() => navigate('/dashboard/vendors')}
            />
            <Flex
                gap="8px"
                overflowX="auto"
                pb="2px"
                css={{
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: '5px' },
                    '&::-webkit-scrollbar-thumb': { background: BORDER, borderRadius: '3px' },
                }}
            >
                {isLoading
                    ? Array.from({ length: SHOP_BY_VENDOR_COUNT }).map((_, i) => <ShopByVendorSkeleton key={i} />)
                    : displayVendors.length === 0
                    ? <Flex w="full" justify="center" align="center" py="16px" color={FAINT} fontSize="11px">No vendors available right now</Flex>
                    : displayVendors.map((v) => (
                        <Flex
                            key={v.id}
                            direction="column"
                            align="center"
                            gap="6px"
                            flexShrink={0}
                            minW="80px"
                            cursor="pointer"
                            onClick={() => goToVendor(v)}
                            p="6px"
                            borderRadius="8px"
                            transition="background 0.15s ease"
                            _hover={{ bg: SURFACE_ALT }}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    goToVendor(v);
                                }
                            }}
                            aria-label={`Shop ${v.name}`}
                        >
                            <VendorAvatar vendor={v} size="52px" fontSize="18px" />
                            <Text fontSize="10.5px" fontWeight="600" color={INK} noOfLines={1} maxW="76px" textAlign="center">
                                {v.name}
                            </Text>
                        </Flex>
                    ))}
            </Flex>
        </Card>
    );
});

/* ─────────────────────────────────────────────────────────────
   TOP VENDORS (grid)
   ───────────────────────────────────────────────────────────── */
const VendorCard = memo(function VendorCard({ vendor, onClick }) {
    return (
        <Flex
            direction="column"
            align="center"
            gap="6px"
            p="8px 6px"
            borderRadius="10px"
            cursor="pointer"
            onClick={onClick}
            border="1px solid"
            borderColor={BORDER}
            bg={SURFACE}
            transition="all 0.15s ease"
            _hover={{ borderColor: NAVY, boxShadow: SHADOW_SM, transform: 'translateY(-2px)' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}
            aria-label={`View ${vendor.name} products`}
        >
            <VendorAvatar vendor={vendor} size="42px" fontSize="14px" />
            <Box textAlign="center" minW="0" w="full">
                <Text fontSize="10.5px" fontWeight="700" color={INK} noOfLines={1}>{vendor.name}</Text>
                {vendor.productCount != null && vendor.productCount > 0 && (
                    <Text fontSize="9.5px" color={FAINT}>{vendor.productCount} items</Text>
                )}
            </Box>
        </Flex>
    );
});

const VendorSkeleton = memo(function VendorSkeleton() {
    return (
        <Box p="8px 6px" borderRadius="10px" border="1px solid" borderColor={BORDER}>
            <Flex direction="column" align="center" gap="6px">
                <SkeletonCircle size="42px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2.5" w="80%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
                <Skeleton h="2" w="50%" borderRadius="3px" startColor={SURFACE_ALT} endColor={BORDER} />
            </Flex>
        </Box>
    );
});

const TopVendors = memo(function TopVendors({ navigate }) {
    const { vendors: rawVendors, isLoading } = useVendors({ per_page: 30 });
     const vendors = useMemo(
        () => rawVendors.filter(isDisplayableVendor).map(normalizeVendor),
        [rawVendors]
    );
    const goToVendor = useGoToVendor();
    const displayVendors = useMemo(() => {
        if (vendors.length <= SHOP_BY_VENDOR_COUNT) return vendors.slice(0, TOP_VENDORS_COUNT);
        return vendors.slice(SHOP_BY_VENDOR_COUNT, SHOP_BY_VENDOR_COUNT + TOP_VENDORS_COUNT);
    }, [vendors]);

    return (
        <Card mb="14px">
            <SectionTitle
                title="Top Vendors"
                subtitle="More verified sellers to explore"
                icon={StoreIcon}
                accent={NAVY}
                lightBg={CYAN}
                onSeeAll={() => navigate('/dashboard/vendors')}
            />
            {isLoading ? (
                <SimpleGrid columns={{ base: 4, md: 6 }} spacing="8px">
                    {Array.from({ length: 6 }).map((_, i) => <VendorSkeleton key={i} />)}
                </SimpleGrid>
            ) : displayVendors.length === 0 ? (
                <Flex direction="column" align="center" justify="center" bg={SURFACE_ALT} borderRadius="10px" p="20px 16px" textAlign="center" border="1px dashed" borderColor={BORDER}>
                    <Flex w="32px" h="32px" borderRadius="8px" bg={SURFACE} align="center" justify="center" mb="8px" color={MUTED} boxShadow={SHADOW_SM}>
                        <StoreIcon />
                    </Flex>
                    <Text fontSize="12px" fontWeight="600" color={INK} mb="1px">That's all the vendors for now</Text>
                    <Text fontSize="10.5px" color={FAINT}>Scroll up to see featured sellers above.</Text>
                </Flex>
            ) : (
                <SimpleGrid columns={{ base: 4, md: 6 }} spacing="8px">
                    {displayVendors.slice(0, 6).map((v) => (
                        <VendorCard key={v.id} vendor={v} onClick={() => goToVendor(v)} />
                    ))}
                </SimpleGrid>
            )}
        </Card>
    );
});

/* ─────────────────────────────────────────────────────────────
   PRODUCT SECTION (with filter state)
   ───────────────────────────────────────────────────────────── */
const ProductSection = memo(function ProductSection({ section, navigate }) {
    // Filter state – preserved from original
    const [filters, setFilters] = useState({ category: '', brand: '', priceRange: '', sortBy: '' });
    const priceMin = filters.priceRange ? filters.priceRange.split('-')[0] : undefined;
    const priceMax = filters.priceRange ? filters.priceRange.split('-')[1] : undefined;

    const { data, isLoading } = useGetProducts({
        per_page: ITEMS_PER_SECTION,
        ...section.query,
        ...(filters.category ? { category_id: filters.category } : {}),
        ...(filters.brand ? { brand: filters.brand } : {}),
        ...(priceMin ? { min_rate: priceMin } : {}),
        ...(priceMax ? { max_rate: priceMax } : {}),
        ...(filters.sortBy ? { price_sort: filters.sortBy } : {}),
    });

    const items = data?.items?.slice(0, ITEMS_PER_SECTION) ?? [];
    const onSeeAll = () => navigate(section.seeAllUrl);
    const onBrowse = () => navigate('/dashboard/products');

    return (
        <Card mb="14px">
            <SectionTitle
                title={section.label}
                subtitle={section.subtitle}
                icon={section.icon}
                accent={section.accent}
                lightBg={section.lightBg}
                badge={section.badgeText}
                onSeeAll={onSeeAll}
            />
            {isLoading ? (
                <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5, xl: 5 }} spacing="8px">
                    {SKELETON_KEYS.map((i) => <ProductSkeleton key={i} />)}
                </SimpleGrid>
            ) : items.length === 0 ? (
                <EmptySection label={section.label} lightBg={section.lightBg} icon={section.icon} onBrowse={onBrowse} />
            ) : (
                <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5, xl: 5 }} spacing="8px">
                    {items.map((item, i) => (
                        <Box key={item.item_id ?? i} position="relative">
                            <ProductItemNew item={item} />
                            {item._backfill && (
                                <Badge
                                    position="absolute"
                                    top="5px"
                                    left="5px"
                                    bg="whiteAlpha.900"
                                    color={MUTED}
                                    fontSize="8.5px"
                                    fontWeight="700"
                                    px="4px"
                                    py="1.5px"
                                    borderRadius="4px"
                                    pointerEvents="none"
                                    boxShadow={SHADOW_SM}
                                >
                                    Related
                                </Badge>
                            )}
                        </Box>
                    ))}
                </SimpleGrid>
            )}
        </Card>
    );
});

/* ─────────────────────────────────────────────────────────────
   RIGHT PANEL (with isOpen prop)
   ───────────────────────────────────────────────────────────── */
const RightPanel = memo(function RightPanel({ navigate, isOpen }) {
    if (!isOpen) return null;
    return (
        <VStack
            display={{ base: 'none', lg: 'flex' }}
            minW="280px"
            maxW="320px"
            bg={SURFACE_ALT}
            p="16px 14px"
            spacing="12px"
            borderLeft="1px solid"
            borderColor={BORDER}
            h="100%"
            overflowY="auto"
            align="stretch"
            flexShrink={0}
        >
            <Card p="14px">
                <Text fontSize="9.5px" color={FAINT} fontWeight="700" letterSpacing="0.12em" mb="5px">NEED A HAND?</Text>
                <Heading fontSize="13px" color={INK} fontWeight="800" mb="5px" fontFamily={FONT}>How dropshipping works</Heading>
                <Text fontSize="10.5px" color={MUTED} mb="10px" lineHeight="1.5">A 2-min walkthrough on how to source, list, and fulfill orders.</Text>
                <Box borderRadius="8px" overflow="hidden" position="relative" cursor="pointer">
                    <Image src={placeholder} w="full" h="120px" objectFit="cover" />
                    <Flex position="absolute" inset="0" align="center" justify="center" bg="blackAlpha.300">
                        <Flex w="36px" h="36px" borderRadius="full" bg="whiteAlpha.900" align="center" justify="center" color={NAVY}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                        </Flex>
                    </Flex>
                </Box>
            </Card>

            <Card p="14px" cursor="pointer" onClick={() => navigate('/dashboard/training')} _hover={{ boxShadow: SHADOW_MD, transform: 'translateY(-2px)', borderColor: NAVY }} transition="transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease">
                <Flex align="center" gap="10px" mb="8px">
                    <Flex w="30px" h="30px" borderRadius="8px" bg={NAVY} color="white" align="center" justify="center" flexShrink={0}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                            <path d="M22 10v6M2 10l10-5 10 5-10 5z M6 12v5c0 1.66 2.69 3 6 3s6-1.34 6-3v-5" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                        </svg>
                    </Flex>
                    <Box minW="0" flex="1">
                        <Text fontSize="9.5px" color={FAINT} fontWeight="700" letterSpacing="0.12em">TRAINING</Text>
                        <Heading fontSize="13px" color={INK} fontWeight="800" fontFamily={FONT} lineHeight="1.2">Agent training hub</Heading>
                    </Box>
                </Flex>
                <Text fontSize="10.5px" color={MUTED} mb="10px" lineHeight="1.5">Get certified, learn how to source, list, and grow your sales.</Text>
                <Button size="sm" w="full" bg={CYAN} color={NAVY} _hover={{ bg: '#C5EFF1' }} borderRadius="7px" fontSize="10.5px" fontWeight="700" h="30px" rightIcon={<ChevronRightIcon />}>Begin training</Button>
            </Card>

            <Card p="14px" bg={`linear-gradient(135deg, ${CYAN} 0%, #E8FAFB 100%)`} border="none">
                <Text fontSize="9.5px" color={NAVY} fontWeight="700" letterSpacing="0.12em" mb="3px" opacity={0.7}>THIS MONTH</Text>
                <Heading fontSize="22px" color={NAVY} fontWeight="800" fontFamily={FONT} mb="2px">₦0</Heading>
                <Text fontSize="10.5px" color={NAVY} opacity={0.75} mb="10px">Commission earned</Text>
                <Button size="sm" bg={NAVY} color="white" _hover={{ bg: NAVY_DEEP }} borderRadius="7px" w="full" fontSize="10.5px" h="30px" onClick={() => navigate('/dashboard/transactions')}>View transactions</Button>
            </Card>

            <Card p="12px 14px" cursor="pointer" onClick={() => openWhatsAppSupport()} _hover={{ boxShadow: SHADOW_MD, transform: 'translateY(-2px)', borderColor: '#25D366' }} transition="transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease">
                <Flex align="center" gap="9px">
                    <Box w="28px" h="28px" borderRadius="7px" bg="#25D366" color="white" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                    </Box>
                    <Box flex="1" minW="0">
                        <Text fontSize="11.5px" fontWeight="700" color={INK}>Live support</Text>
                        <Text fontSize="10px" color={FAINT}>Chat on WhatsApp · Avg. 5 min</Text>
                    </Box>
                    <Box color={FAINT} flexShrink={0}><ChevronRightIcon /></Box>
                </Flex>
            </Card>
        </VStack>
    );
});

/* ─────────────────────────────────────────────────────────────
   PANEL TOGGLE BUTTON
   ───────────────────────────────────────────────────────────── */
const PanelToggle = ({ isOpen, onToggle }) => (
    <Tooltip label={isOpen ? 'Hide sidebar' : 'Show sidebar'} placement="left" fontSize="11px" bg="gray.700" borderRadius="6px">
        <Box
            as="button"
            onClick={onToggle}
            display={{ base: 'none', lg: 'flex' }}
            alignItems="center"
            justifyContent="center"
            position="fixed"
            right={isOpen ? '328px' : '12px'}
            top="50%"
            transform="translateY(-50%)"
            zIndex={400}
            w="26px"
            h="48px"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="8px"
            boxShadow="0 2px 8px rgba(0,0,0,0.10)"
            color="gray.400"
            _hover={{ bg: NAVY, color: 'white', borderColor: NAVY }}
            style={{ transition: 'right 0.25s ease, background 0.15s ease, color 0.15s ease' }}
        >
            {isOpen ? <PanelCloseIcon /> : <PanelOpenIcon />}
        </Box>
    </Tooltip>
);

/* ─────────────────────────────────────────────────────────────
   MAIN DASHBOARD
   ───────────────────────────────────────────────────────────── */
const LargeDashboard = () => {
    const navigate = useNavigate();
    const [isPanelOpen, setIsPanelOpen] = useState(true);
    const { flattenedUser: details } = useGetUserDetails();

    const displayName = useMemo(
        () => details?.attributes?.first_name || details?.first_name || details?.email?.split('@')[0] || 'there',
        [details]
    );

    return (
        <PreOffersContainer>
            <Flex
                flex="1"
                minW="0"
                h="full"
                flexDir="column"
                px={{ base: '12px', lg: '14px', xl: '18px' }}
                py={{ base: '14px', md: '16px' }}
                pb={{ base: '2rem', lg: '3rem' }}
                overflowY="auto"
                bg={SURFACE_ALT}
                fontFamily={FONT}
                data-dash-scroll
            >
                <GreetingBanner name={displayName} navigate={navigate} />
                <ShopByVendor navigate={navigate} />
                <ProductSection section={NEW_ARRIVALS_CONFIG} navigate={navigate} />
                <TopVendors navigate={navigate} />
                {TRAILING_SECTIONS.map((section) => (
                    <ProductSection key={section.key} section={section} navigate={navigate} />
                ))}
                <Box>
                    <Brands />
                </Box>
            </Flex>

            <RightPanel navigate={navigate} isOpen={isPanelOpen} />
            <PanelToggle isOpen={isPanelOpen} onToggle={() => setIsPanelOpen(v => !v)} />
        </PreOffersContainer>
    );
};

export default LargeDashboard;

export const arrow = (
    <svg width="12" height="6" viewBox="0 0 12 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            d="M11.8625 2.65749C11.8624 2.65735 11.8623 2.65718 11.8621 2.65703L9.41278 0.140902C9.22928 -0.0475903 8.9325 -0.0468889 8.74985 0.142547C8.56722 0.331958 8.56793 0.638321 8.75142 0.826837L10.3959 2.51613H0.46875C0.209859 2.51613 0 2.73276 0 3C0 3.26724 0.209859 3.48387 0.46875 3.48487H10.3959L8.75144 5.17316C8.56795 5.36168 8.56725 5.66804 8.74987 5.85745C8.93252 6.04691 9.22933 6.04757 9.4128 5.8591L11.8621 3.34297C11.8623 3.34282 11.8624 3.34265 11.8625 3.34251C12.0461 3.15336 12.0455 2.84601 11.8625 2.65749Z"
            fill="white"
        />
    </svg>
);